import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notification } from "antd";
import OutletService from "services/OutletService";
export const initialState = {
  data: [],
  dataSelect: [],
  dataSelectReceiver: [],
  dataDetail: [],
  dataDelete: null,
  dataCashReport: null,
  loading: false,
  error: null,
  hasMore: false,
  hasMoreReceiver: false,
  status: "idle",
  statusReceiver: "idle",
  redirect: false,
};

export const fetchOutletData = createAsyncThunk(
  "outlet/fetchOutletData",
  async (data, { rejectWithValue }) => {
    try {
      const { page, pageSize, sort, searchText, district, state } = data;
      const response = await OutletService.get({
        page,
        pageSize,
        sort,
        searchText,
        district,
        state,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const fetchOutletDataSelect = createAsyncThunk(
  "outlet/fetchOutletDataSelect",
  async (data, { rejectWithValue }) => {
    try {
      const { page, pageSize, sort, searchText } = data;
      const response = await OutletService.get({
        page,
        pageSize,
        sort,
        searchText,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchOutletDataSelectReceiver = createAsyncThunk(
  "outlet/fetchOutletDataSelectReceiver",
  async (data, { rejectWithValue }) => {
    try {
      const { page, pageSize, sort, searchText } = data;
      const response = await OutletService.get({
        page,
        pageSize,
        sort,
        searchText,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchOutletDataSelectByDist = createAsyncThunk(
  "outlet/fetchOutletDataSelectByDist",
  async (data, { rejectWithValue }) => {
    try {
      const { page, pageSize, searchText, districts } = data;
      const response = await OutletService.getByDistrict({
        page,
        pageSize,
        searchText,
        districts,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchOutletDetail = createAsyncThunk(
  "outlet/fetchOutletDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await OutletService.getById(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchOutletCashReport = createAsyncThunk(
  "outlet/fetchOutletCashReport",
  async (id, { rejectWithValue }) => {
    try {
      const response = await OutletService.getCashReport(id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const createOutletData = createAsyncThunk(
  "outlet/createOutletData",
  async (data, { rejectWithValue }) => {
    try {
      const { name, addr, district, detail } = data;
      const response = await OutletService.createOutlet({
        name,
        addr,
        district,
        detail,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const updateOutletData = createAsyncThunk(
  "outlet/updateOutletData",
  async (data, { rejectWithValue }) => {
    try {
      const { id, name, addr, district, detail } = data;
      const response = await OutletService.updateOutlet({
        id,
        name,
        addr,
        district,
        detail,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const deleteOutletData = createAsyncThunk(
  "outlet/deleteOutletData",
  async (data, { rejectWithValue }) => {
    try {
      const { id, confirm } = data;
      const response = await OutletService.deleteOutlet(id, confirm);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const outletSlice = createSlice({
  name: "outlet",
  initialState,
  reducers: {
    resetDataSelectOutlet: (state) => {
      state.dataSelect = [];
    },
    resetDataSelectOutletReceiver: (state) => {
      state.dataSelectReceiver = [];
    },
    resetDataDeleteOutlet: (state) => {
      state.dataDelete = null;
    },
    showLoading: (state) => {
      state.loading = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOutletData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOutletData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
      })
      .addCase(fetchOutletData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchOutletDataSelect.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchOutletDataSelect.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.dataSelect.push(...action.payload.data.data);
        const uniqueData = Array.from(
          new Set(state.dataSelect.map((item) => item.name))
        ).map((name) => {
          return state.dataSelect.find((item) => item.name === name);
        });
        state.dataSelect = uniqueData;
        state.hasMore = action.payload.data.next_page_url;
      })
      .addCase(fetchOutletDataSelect.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchOutletDataSelectReceiver.pending, (state) => {
        state.statusReceiver = "loading";
      })
      .addCase(fetchOutletDataSelectReceiver.fulfilled, (state, action) => {
        state.statusReceiver = "succeeded";
        state.dataSelectReceiver.push(...action.payload.data.data);
        const uniqueData = Array.from(
          new Set(state.dataSelectReceiver.map((item) => item.name))
        ).map((name) => {
          return state.dataSelectReceiver.find((item) => item.name === name);
        });
        state.dataSelectReceiver = uniqueData;
        state.hasMoreReceiver = action.payload.data.next_page_url;
      })
      .addCase(fetchOutletDataSelectReceiver.rejected, (state, action) => {
        state.statusReceiver = "failed";
        state.error = action.payload;
      })
      .addCase(fetchOutletDataSelectByDist.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchOutletDataSelectByDist.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.dataSelect.push(...action.payload.data.data);
        const uniqueData = Array.from(
          new Set(state.dataSelect.map((item) => item.name))
        ).map((name) => {
          return state.dataSelect.find((item) => item.name === name);
        });
        state.dataSelect = uniqueData;
        state.hasMore = action.payload.data.next_page_url;
      })
      .addCase(fetchOutletDataSelectByDist.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchOutletDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOutletDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.dataDetail = action.payload.data;
        state.error = null;
      })
      .addCase(fetchOutletDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOutletCashReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOutletCashReport.fulfilled, (state, action) => {
        state.loading = false;
        state.dataCashReport = action.payload.data;
        state.error = null;
      })
      .addCase(fetchOutletCashReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createOutletData.pending, (state) => {
        state.loading = true;
        state.redirect = false;
      })
      .addCase(createOutletData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.redirect = true;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(createOutletData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateOutletData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOutletData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        notification.success({ message: "Proses Berhasil" });
      })
      .addCase(updateOutletData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteOutletData.pending, (state) => {
        state.redirect = false;
        state.loading = true;
      })
      .addCase(deleteOutletData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.status === 200) state.dataDelete = action.payload;
        else {
          state.redirect = true;
          notification.success({ message: "Proses Berhasil" });
        }
      })
      .addCase(deleteOutletData.rejected, (state, action) => {
        state.redirect = false;
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  showLoading,
  resetDataSelectOutlet,
  resetDataSelectOutletReceiver,
  resetDataDeleteOutlet,
} = outletSlice.actions;

export default outletSlice.reducer;
